/**
 * Fallback body for card types whose proper Body component hasn't
 * shipped yet, or for unknown types from a future backend.
 *
 * Renders a compact key/value dump so the chat doesn't crash and so
 * during incremental B-frontend rollout we can still see card content
 * for types we haven't implemented yet.
 */
import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';

interface UnknownCardBodyProps {
  cardType: string;
  content: Record<string, unknown>;
}

export const UnknownCardBody: React.FC<UnknownCardBodyProps> = ({
  cardType,
  content,
}) => {
  // Drop schema_version and type from the dump — they're metadata, not content.
  const entries = Object.entries(content).filter(
    ([k]) => k !== 'schema_version' && k !== 'type'
  );
  return (
    <Stack spacing={1}>
      <Alert severity="info" sx={{ py: 0.5, fontSize: 12 }}>
        Body component for <code>{cardType}</code> not yet implemented; showing
        raw content.
      </Alert>
      <Stack spacing={0.5}>
        {entries.map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: 'text.secondary',
                fontFamily: 'monospace',
                minWidth: 120,
              }}
            >
              {k}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontFamily: 'monospace',
                wordBreak: 'break-word',
                color: 'text.primary',
              }}
            >
              {formatValue(v)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

function formatValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
